import { el, clear } from '../dom';
import { gicon, type IconName } from '../ui/icons';
import { CHAR_SLOTS } from './manifest.generated';
import { composeCharacter, layerThumb, randomAppearance, defaultAppearance, type Appearance } from './compositor';
import {
  HERO_PRESETS, SKIN_TONES, PREVIEW_CROP, buildHeroAppearance, matchHeroPreset,
  nearestSwatch, swatchForLabel, styleOfFile, prettyLabel, colourName,
} from './presets';

/**
 * Post-registration character creator. A game-themed wardrobe with a LIVE,
 * animated paper-doll: pick a hero archetype, then restyle slot-by-slot and
 * recolour skin / hair / clothes. Calls onDone with the chosen appearance.
 *
 * Preview sizing: the pack's player frames are 64×64 but the character only
 * occupies ~24×28 px inside them (see PREVIEW_CROP), so the preview draws a
 * fixed crop window rather than the whole frame — that window is what makes
 * the hero read big instead of a stamp floating in empty air. The window is
 * FIXED (not a per-frame bounding box) so the walk cycle doesn't jitter.
 */

// ── Animation layout on the Player sheet (mirrors engine/map/player.ts) ───────
type Facing = 'down' | 'up' | 'left' | 'right';
const ROW = {
  idle: { down: 0, side: 1, up: 2 },
  walk: { down: 3, side: 4, up: 5 },
} as const;
const MAX_FRAMES = 9;
const FPS = { idle: 4, walk: 8 } as const;

// A pack sprite per slot so the wardrobe reads at a glance. These were emoji
// (🧍💇🎩👕👖🥾) rendering as system glyphs next to pixel art; the icons are
// sliced from the pack by scripts/build-creator-icons.py.
const SLOT_ICON: Record<string, IconName> = {
  skin: 'face', hair: 'hair', headgear: 'hat', shirt: 'shirt', pants: 'legs', shoes: 'boots',
};

/** Row + horizontal flip for a facing. Left reuses the side row, mirrored. */
function facingRow(mode: 'idle' | 'walk', f: Facing): { row: number; flip: boolean } {
  if (f === 'up') return { row: ROW[mode].up, flip: false };
  if (f === 'down') return { row: ROW[mode].down, flip: false };
  if (f === 'left') return { row: ROW[mode].side, flip: true };
  return { row: ROW[mode].side, flip: false }; // right
}

/** True if a composed 64×64 frame has any visible pixel (to find a row's length). */
function frameHasPixels(cv: HTMLCanvasElement): boolean {
  const d = cv.getContext('2d')!.getImageData(0, 0, cv.width, cv.height).data;
  for (let i = 3; i < d.length; i += 4) if (d[i] > 8) return true;
  return false;
}

/** Crop a composed 64×64 frame down to the character window. */
function cropToHero(src: HTMLCanvasElement): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = PREVIEW_CROP.w; c.height = PREVIEW_CROP.h;
  const x = c.getContext('2d')!;
  x.imageSmoothingEnabled = false;
  x.drawImage(src, PREVIEW_CROP.x, PREVIEW_CROP.y, PREVIEW_CROP.w, PREVIEW_CROP.h, 0, 0, PREVIEW_CROP.w, PREVIEW_CROP.h);
  return c;
}

/** Hero name is persisted separately so the Appearance type stays a pure slot map. */
const nameKey = (who: string) => 'terraboma:heroname:' + who;
function loadHeroName(who: string): string {
  try { return localStorage.getItem(nameKey(who)) ?? ''; } catch { return ''; }
}
function saveHeroName(who: string, name: string): void {
  try { localStorage.setItem(nameKey(who), name.trim()); } catch { /* private mode */ }
}

export function renderCharacterCreator(root: HTMLElement, username: string, onDone: (a: Appearance) => void): () => void {
  clear(root);
  let appearance: Appearance = buildHeroAppearance(HERO_PRESETS[0]) ?? defaultAppearance();
  let activeSlot = 'skin';
  let facing: Facing = 'down';
  let moving = false;
  let heroName = loadHeroName(username) || (username.includes('@') ? username.split('@')[0] : username);

  // ── Shell ───────────────────────────────────────────────────────────────────
  // Three bands: header, a SCROLLING body, and a pinned footer. The footer is
  // outside the scroller on purpose — on a landscape phone the wardrobe is
  // taller than the viewport, and Confirm must never be something you have to
  // scroll to find.
  const shell = el('div', 'game-font cc-bg h-full w-full flex flex-col overflow-hidden');

  const header = el('header', 'tb-header shrink-0 flex items-center gap-2 px-3 md:px-5 py-2 md:py-3', {},
    gicon('shroom', 22),
    el('div', 'text-gold text-base md:text-xl', { style: 'text-shadow:0 2px 0 #000' }, 'Create your Farmer'),
    el('div', 'hidden lg:block text-xs text-text-dim', {}, 'Pick a hero, dress them up, then step into Terraboma'),
    el('div', 'flex-1'),
    el('span', 'hidden sm:inline text-[12px] text-text-dim', {}, `@${username}`));

  // ONE scroller for the whole body, laid out as hero-left / choices-right at
  // EVERY width — the columns never stack. At 320px the hero column is only 96px
  // wide, but it is a real column, and that is deliberate: the hero is
  // `sticky top-0` inside the scroller, and a sticky element is only pinned while
  // its own grid row is on screen. Stacking the columns on a phone would give the
  // stage a short row of its own and the pin would let go after one flick —
  // exactly the "scroll down to the swatch, scroll back up to see it" problem
  // this is meant to remove. Sharing one grid row with the choices makes the
  // row as tall as the whole wardrobe, so the doll stays put for the entire
  // scroll. Everything that does not have to be pinned (d-pad, dice, name,
  // wardrobe) lives in the right column, so the pinned band stays a doll and
  // nothing else.
  const body = el('div', 'flex-1 min-h-0 overflow-y-auto grid grid-cols-[96px_minmax(0,1fr)] sm:grid-cols-[minmax(150px,28%)_minmax(0,1fr)] gap-2 sm:gap-3 p-2 sm:p-3 pixel-scroll');

  // ═══ LEFT: animated stage ════════════════════════════════════════════════════
  // Canvas pixels = the crop window; CSS scales it up with nearest-neighbour.
  // The height is capped by vh so the pinned band never eats a short landscape
  // screen, and so the choices beside it stay reachable.
  const disp = el('canvas', 'block', {
    style: `width:auto;height:clamp(88px,18vh,240px);max-width:100%;aspect-ratio:${PREVIEW_CROP.w}/${PREVIEW_CROP.h};image-rendering:pixelated`,
  }) as HTMLCanvasElement;
  disp.width = PREVIEW_CROP.w; disp.height = PREVIEW_CROP.h;
  const dctx = disp.getContext('2d')!; dctx.imageSmoothingEnabled = false;

  const scene = el('div', 'cc-stage relative w-full max-w-[340px] overflow-hidden flex flex-col items-center justify-end pb-2 pt-3 sm:pb-3 sm:pt-4', {},
    disp,
    el('div', 'rounded-[50%] -mt-2', { style: 'width:52%;height:12px;background:radial-gradient(50% 50%, rgba(0,0,0,.5), transparent 70%)' }));

  // Direction pad — pack caret sprites, not ▲▼◀▶ text glyphs. 44px targets.
  const turnBtn = (icon: IconName, f: Facing, rotate = 0) => {
    const g = gicon(icon, 14);
    if (rotate) g.style.transform = `rotate(${rotate}deg)`;
    return el('button', 'tb-pixel-btn w-11 h-11 grid place-items-center',
      { title: f, onClick: () => { facing = f; recompose(); paintDirState(); } }, g) as HTMLButtonElement;
  };
  const bUp = turnBtn('caretUp', 'up'), bDown = turnBtn('caretDown', 'down');
  const bLeft = turnBtn('caretLeft', 'left'), bRight = turnBtn('caretRight', 'right');
  // The pad's centre cell is the stand/walk toggle: it is the one control that
  // belongs *inside* the pad (it changes how the hero moves, not where it looks)
  // and it saves a row of chrome on a short landscape screen.
  const walkToggle = el('button', 'tb-pixel-btn w-11 h-11 grid place-items-center',
    { title: 'Walk', onClick: () => { moving = !moving; recompose(); paintDirState(); } }) as HTMLButtonElement;
  const dpad = el('div', 'grid gap-1', { style: 'grid-template-columns:repeat(3,auto);justify-content:center' },
    el('span'), bUp, el('span'),
    bLeft, walkToggle, bRight,
    el('span'), bDown, el('span'));

  const iconBtn = (icon: IconName, label: string, onClick: () => void) =>
    el('button', 'tb-pixel-btn px-3 min-h-[44px] text-sm flex items-center gap-1.5',
      { onClick }, gicon(icon, 15), el('span', '', {}, label));
  const randomBtn = iconBtn('dice', 'Random', () => { appearance = randomAppearance(); refreshAll(); });
  const resetBtn = iconBtn('person', 'Reset', () => { appearance = defaultAppearance(); refreshAll(); });

  const nameInput = el('input', 'cc-input w-full text-center px-3 py-2 min-h-[44px]',
    { value: heroName, maxLength: 18, placeholder: 'Name your farmer', spellcheck: false }) as HTMLInputElement;
  nameInput.addEventListener('input', () => { heroName = nameInput.value; });

  const stage = el('div', 'cc-panel sticky top-0 z-20 self-start flex flex-col items-center gap-1 p-1.5 sm:p-3', {},
    scene, el('div', 'cc-label text-[10px] sm:text-[13px]', {}, 'Preview'));

  // ═══ RIGHT: view controls + hero picker + wardrobe ═══════════════════════════
  // The d-pad rides here rather than under the doll: at 96px the left column
  // cannot hold three 44px cells across, and turning the hero is a thing you do
  // *while* choosing, so it belongs with the choices.
  const viewRow = el('div', 'flex items-center gap-2 flex-wrap mb-3', {}, dpad, randomBtn, resetBtn);

  const heroRow = el('div', 'flex gap-2 overflow-x-auto pb-2 px-0.5 pixel-scroll');
  const slotNav = el('div', 'flex flex-wrap gap-1 mb-2');
  // "What you are changing" — the slot, the garment and the colour, in words.
  // The wardrobe used to answer none of those: the only feedback for a choice
  // was a 24px doll and a highlighted square.
  const nowEditing = el('div', 'cc-panel-sunk flex items-center gap-2 px-2.5 py-2 mb-3 min-h-[44px]');
  const slotPanel = el('div', 'pr-1');
  const controls = el('div', 'cc-panel flex flex-col p-2 sm:p-3 md:p-4 min-w-0', {},
    viewRow,
    el('div', 'cc-label mb-2', {}, 'Choose your hero'),
    heroRow,
    el('div', 'w-full max-w-[280px] flex flex-col gap-1.5 mt-2', {},
      el('div', 'cc-label', {}, 'Farmer name'),
      nameInput),
    el('div', 'h-[3px] my-3 rounded', { style: 'background:linear-gradient(90deg,transparent,#5a4632 15%,#5a4632 85%,transparent)' }),
    slotNav, nowEditing, slotPanel);

  // ═══ FOOTER: always reachable, never inside a scroller ═══════════════════════
  const confirmBtn = el('button', 'cc-confirm px-5 py-2.5 min-h-[44px] text-base flex items-center gap-2 whitespace-nowrap',
    { onClick: () => { saveHeroName(username, heroName || username); onDone(appearance); } },
    gicon('check', 16), el('span', '', {}, 'Enter Terraboma'), gicon('shroom', 16));
  const footer = el('div', 'shrink-0 flex items-center gap-3 px-3 md:px-5 py-2 border-t-[3px] border-[#4a3626]',
    { style: 'background:linear-gradient(180deg,#322820,#241d18)' },
    // The wardrobe is real: Settings → Game → Wardrobe re-opens this same editor.
    el('span', 'hidden md:inline text-xs text-text-dim', {}, 'You can restyle later in Settings → Wardrobe.'),
    el('div', 'flex-1'), confirmBtn);

  body.append(stage, controls);
  shell.append(header, body, footer);
  root.append(shell);

  // ── Live animation loop ───────────────────────────────────────────────────────
  let frames: HTMLCanvasElement[] = [];
  let flip = false;
  let composeToken = 0;
  let rafId = 0;
  let last = 0, idx = 0, acc = 0;
  let disposed = false;

  function recompose() {
    const token = ++composeToken;
    const mode = moving ? 'walk' : 'idle';
    const { row, flip: fl } = facingRow(mode, facing);
    flip = fl;
    void (async () => {
      const out: HTMLCanvasElement[] = [];
      for (let f = 0; f < MAX_FRAMES; f++) {
        const cv = await composeCharacter(appearance, { fx: f, fy: row, scale: 1 });
        if (frameHasPixels(cv)) out.push(cropToHero(cv)); else if (f > 0) break; // row ended
      }
      if (token !== composeToken) return; // superseded by a newer change
      frames = out.length ? out : [cropToHero(await composeCharacter(appearance, { fx: 0, fy: row, scale: 1 }))];
      idx = 0; acc = 0;
    })();
  }

  function tick(t: number) {
    rafId = requestAnimationFrame(tick);
    if (!frames.length) { last = t; return; }
    const dt = t - (last || t); last = t;
    acc += dt;
    const step = 1000 / (moving ? FPS.walk : FPS.idle);
    if (acc >= step) { acc = 0; idx = (idx + 1) % frames.length; }
    const fr = frames[Math.min(idx, frames.length - 1)];
    dctx.clearRect(0, 0, disp.width, disp.height);
    dctx.save();
    if (flip) { dctx.translate(disp.width, 0); dctx.scale(-1, 1); }
    dctx.drawImage(fr, 0, 0);
    dctx.restore();
  }
  rafId = requestAnimationFrame(tick);

  function paintDirState() {
    for (const [b, f] of [[bUp, 'up'], [bDown, 'down'], [bLeft, 'left'], [bRight, 'right']] as [HTMLButtonElement, Facing][]) {
      b.style.borderColor = facing === f ? '#ffd97a' : '';
      b.style.background = facing === f ? '#503d2c' : '';
    }
    // Footprints = "you are walking, press to stand"; the standing figure the
    // inverse. Icon-only, so the 44px cell never has to fit a word.
    clear(walkToggle);
    walkToggle.title = moving ? 'Stand still' : 'Walk';
    walkToggle.append(gicon(moving ? 'person' : 'footprints', 16));
  }

  // ── Hero archetype picker ─────────────────────────────────────────────────────
  function renderHeroRow() {
    const active = matchHeroPreset(appearance);
    clear(heroRow);
    for (const p of HERO_PRESETS) {
      const on = p.id === active;
      const card = el('button',
        'cc-card shrink-0 w-[96px] px-2 pt-2 pb-1.5 flex flex-col items-center gap-1 ' + (on ? 'cc-card-on' : ''),
        { title: p.blurb, onClick: () => { appearance = buildHeroAppearance(p); refreshAll(); } });
      const holder = el('div', 'w-full grid place-items-center', { style: 'height:64px' });
      card.append(holder,
        el('span', 'text-[12px] leading-tight ' + (on ? 'text-gold' : 'text-text'), {}, p.label),
        el('span', 'text-[10px] leading-tight text-text-dim text-center', {}, p.blurb));
      void composeCharacter(buildHeroAppearance(p), { fx: 0, fy: 0, scale: 1 }).then((cv) => {
        if (disposed) return;
        const c = cropToHero(cv);
        c.style.imageRendering = 'pixelated';
        c.style.height = '64px';
        c.style.width = 'auto';
        holder.append(c);
      });
      heroRow.append(card);
    }
  }

  // ── Wardrobe rendering ────────────────────────────────────────────────────────
  function renderSlotNav() {
    clear(slotNav);
    for (const s of CHAR_SLOTS) {
      const active = s.id === activeSlot;
      slotNav.append(el('button',
        'tb-tab px-3 min-h-[44px] text-sm flex items-center gap-1.5 ' + (active ? 'tb-tab-on' : 'tb-tab-off'),
        { onClick: () => { activeSlot = s.id; renderSlotNav(); renderSlotPanel(); } },
        gicon(SLOT_ICON[s.id] ?? 'chest', 16), el('span', '', {}, s.label)));
    }
  }

  /**
   * A colour choice: painted chip + its NAME + a check when selected.
   *
   * It used to be a bare 40px square whose only identity was its title attribute
   * — invisible on touch, and to a colour-blind player the grid was a row of
   * indistinguishable greys with no way to tell which one was picked. Colour is
   * now never the only channel: name, checkmark and gold frame all carry it.
   */
  function colourTile(hex: string, name: string, on: boolean, onClick: () => void): HTMLElement {
    const b = el('button',
      'cc-card flex items-center gap-2 min-h-[44px] px-2 py-1 text-left ' + (on ? 'cc-card-on' : ''),
      { title: name, onClick },
      el('span', 'shrink-0 rounded-[3px] border-2 border-[#1b120c]',
        { style: `width:26px;height:26px;background:${hex}` }),
      el('span', 'flex-1 min-w-0 text-[11px] leading-tight ' + (on ? 'text-gold' : 'text-text'), {}, name),
      on ? gicon('check', 12) : el('span', 'w-3 shrink-0'));
    b.setAttribute('aria-pressed', String(on));
    return b;
  }

  /** Swatch grids: two per row at 320px, more as the column widens. */
  const swatchGrid = () => el('div', 'grid gap-2 mb-5 grid-cols-1 min-[420px]:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4');

  function sectionLabel(text: string) {
    return el('div', 'cc-label mb-2', {}, text);
  }

  /** The words for the current choice, shown in the "now editing" strip. */
  function describeChoice(slotId: string): { item: string; colour: string; hex: string } {
    const choice = appearance[slotId] ?? { file: null, color: null };
    if (slotId === 'skin') {
      const cur = choice.color ?? '#ffffff';
      const exact = SKIN_TONES.find((t) => t.hex.toLowerCase() === cur.toLowerCase());
      return { item: 'Body', colour: exact ? exact.label : `Custom · ${colourName(cur)}`, hex: cur };
    }
    if (!choice.file) return { item: 'None', colour: 'not worn', hex: '#1b120c' };
    const st = styleOfFile(slotId, choice.file);
    const preset = st.presets.find((p) => p.file === choice.file);
    const item = prettyLabel(st.label);
    if (choice.color) return { item, colour: `Custom · ${colourName(choice.color)}`, hex: choice.color };
    const label = prettyLabel(preset?.label ?? '');
    return { item, colour: label || '—', hex: swatchForLabel(preset?.label ?? '') };
  }

  function renderNowEditing() {
    const slot = CHAR_SLOTS.find((s) => s.id === activeSlot)!;
    const d = describeChoice(slot.id);
    clear(nowEditing);
    nowEditing.append(
      gicon(SLOT_ICON[slot.id] ?? 'chest', 18),
      el('div', 'min-w-0 flex-1 leading-tight', {},
        el('div', 'text-[11px] text-text-dim', {}, `Editing ${slot.label.toLowerCase()}`),
        el('div', 'text-[13px] text-gold truncate', {}, `${d.item} · ${d.colour}`)),
      el('span', 'shrink-0 rounded-[3px] border-2 border-[#1b120c]',
        { style: `width:22px;height:22px;background:${d.hex}` }));
  }

  function renderSlotPanel() {
    const slot = CHAR_SLOTS.find((s) => s.id === activeSlot)!;
    const choice = appearance[slot.id] ?? { file: null, color: null };
    const curStyle = styleOfFile(slot.id, choice.file);
    clear(slotPanel);
    renderNowEditing();

    // ── Skin is a pure tint slot: one body sheet, a tone palette + custom picker.
    if (slot.id === 'skin') {
      const cur = choice.color ?? '#ffffff';
      const near = nearestSwatch(cur, SKIN_TONES);
      slotPanel.append(sectionLabel('Skin tone'));
      const row = swatchGrid();
      for (const tone of SKIN_TONES) {
        row.append(colourTile(tone.hex, tone.label, near.hex === tone.hex, () => {
          appearance.skin = { file: choice.file, color: tone.hex === '#ffffff' ? null : tone.hex };
          refreshAll();
        }));
      }
      slotPanel.append(row);
      appendCustomColour(slot.id, choice.file, cur, 'Tints the skin tone');
      return;
    }

    // Style picker (only when the slot has more than one style family).
    if (slot.styles.length > 1) {
      slotPanel.append(sectionLabel('Style'));
      const styleRow = el('div', 'flex flex-wrap gap-2 mb-4');
      for (const st of slot.styles) {
        const active = st.id === curStyle.id && !!choice.file;
        const cell = el('button',
          'cc-card px-2 pt-1.5 pb-1 flex flex-col items-center gap-1 ' + (active ? 'cc-card-on' : ''),
          { onClick: () => { appearance[slot.id] = { file: st.presets[0].file, color: choice.color }; refreshAll(); } });
        void layerThumb(st.presets[0].file, 1).then((cv) => {
          if (disposed) return;
          const c = cropToHero(cv);
          c.style.imageRendering = 'pixelated'; c.style.height = '52px'; c.style.width = 'auto';
          cell.prepend(c);
        });
        cell.append(el('span', 'text-[11px] ' + (active ? 'text-gold' : 'text-text-dim'), {}, prettyLabel(st.label)));
        styleRow.append(cell);
      }
      slotPanel.append(styleRow);
    }

    // Colour chips — these swap in the pack's real recoloured sheets (no filter),
    // which is why they look correct at every shading step.
    slotPanel.append(sectionLabel('Colour'));
    const swatchRow = swatchGrid();
    if (slot.optional) {
      const none = el('button',
        'cc-card flex items-center gap-2 min-h-[44px] px-2 py-1 text-left ' + (!choice.file ? 'cc-card-on' : ''),
        { title: 'Wear nothing in this slot', onClick: () => { appearance[slot.id] = { file: null, color: null }; refreshAll(); } },
        el('span', 'shrink-0 rounded-[3px] border-2 border-[#1b120c] grid place-items-center text-[10px] text-text-dim',
          { style: 'width:26px;height:26px;background:#1b120c' }, '—'),
        el('span', 'flex-1 min-w-0 text-[11px] leading-tight ' + (!choice.file ? 'text-gold' : 'text-text'), {}, 'None'),
        !choice.file ? gicon('check', 12) : el('span', 'w-3 shrink-0'));
      none.setAttribute('aria-pressed', String(!choice.file));
      swatchRow.append(none);
    }
    for (const preset of curStyle.presets) {
      const active = choice.file === preset.file && !choice.color;
      swatchRow.append(colourTile(swatchForLabel(preset.label), prettyLabel(preset.label), active,
        () => { appearance[slot.id] = { file: preset.file, color: null }; refreshAll(); }));
    }
    slotPanel.append(swatchRow);

    if (slot.recolor) {
      appendCustomColour(slot.id, choice.file ?? curStyle.presets[0].file, choice.color ?? '#8a5a3c',
        'Recolours — keeps the shading');
    }
  }

  /** Custom hue/tint picker shared by every recolourable slot. */
  function appendCustomColour(slotId: string, file: string | null, current: string, hint: string) {
    const picker = el('input', 'cc-swatch cursor-pointer p-0 bg-transparent',
      { type: 'color', value: current }) as HTMLInputElement;
    // A colour input is a hex code and nothing else, so it gets the same naming
    // treatment as the chips — "#8a5a3c" tells a player nothing, "Brown" does.
    const readout = el('span', 'text-xs text-gold', {}, colourName(current));
    picker.addEventListener('input', () => {
      appearance[slotId] = { file, color: picker.value };
      readout.textContent = colourName(picker.value);
      recompose(); // live-update the doll only (don't rebuild the panel per drag)
    });
    picker.addEventListener('change', () => { renderHeroRow(); renderSlotPanel(); });
    slotPanel.append(
      sectionLabel('Custom colour'),
      el('div', 'flex items-center gap-3 flex-wrap min-h-[44px]', {},
        picker,
        el('div', 'flex flex-col leading-tight', {},
          readout,
          el('span', 'text-xs text-text-dim', {}, hint)),
        el('button', 'tb-pixel-btn ml-auto px-3 min-h-[44px] text-xs',
          { onClick: () => { appearance[slotId] = { file, color: null }; refreshAll(); } }, 'reset')));
  }

  function refreshAll() { renderHeroRow(); renderSlotNav(); renderSlotPanel(); recompose(); }
  refreshAll();
  paintDirState();

  return () => { disposed = true; cancelAnimationFrame(rafId); composeToken++; clear(root); };
}
